import { parseErrorResponseBody } from "../errors/SDKSupernovaError";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Bridge implementation
export class DataBridge {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(conf) {
        var _a, _b, _c, _d;
        this.isPreview = false;
        this.jobId = null;
        this.authToken = conf.accessToken;
        this.apiUrl = conf.apiUrl;
        this.aiApiUrl = conf.aiApiUrl;
        this.apiVersion = conf.apiVersion;
        this.target = conf.target;
        this.customFetchLib = (_a = conf.customFetchLib) !== null && _a !== void 0 ? _a : null;
        this.customProxyLib = (_b = conf.customProxyLib) !== null && _b !== void 0 ? _b : null;
        if (conf.jobId) {
            this.jobId = conf.jobId;
        }
        if (conf.isPreview) {
            this.isPreview = conf.isPreview;
        }
        this.requestHook = conf.requestHook;
        this.debugRequestObserver = (_c = conf.debugRequestObserver) !== null && _c !== void 0 ? _c : null;
        this.debugResponseObserver = (_d = conf.debugResponseObserver) !== null && _d !== void 0 ? _d : null;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Requests
    async delete(endpoint, payload) {
        return this.request(endpoint, payload, "DELETE");
    }
    async get(endpoint) {
        return this.request(endpoint, undefined, "GET");
    }
    async getStreamed(endpoint) {
        return this.streamedRequest(endpoint, undefined, "GET");
    }
    async getAsData(endpoint) {
        return this.request(endpoint, undefined, "GET", true);
    }
    async put(endpoint, payload) {
        return this.request(endpoint, payload, "PUT");
    }
    async post(endpoint, payload) {
        return this.request(endpoint, payload, "POST");
    }
    async mcp(endpoint, toolName, payload) {
        const initializeResult = await this.mcpRequest(endpoint, {
            jsonrpc: "2.0",
            id: 1,
            method: "initialize",
            params: {
                protocolVersion: "2025-06-18",
                capabilities: {
                    tools: {},
                    resources: {},
                },
                clientInfo: {
                    name: "supernova-sdk",
                    version: "1.0",
                },
            },
        });
        const toolCallResult = await this.mcpRequest(endpoint, {
            jsonrpc: "2.0",
            id: 2,
            method: "tools/call",
            params: {
                name: toolName,
                arguments: payload,
            },
        }, initializeResult.sessionId);
        return this.parseMCPToolText(toolCallResult.message);
    }
    async postStreamed(endpoint, payload) {
        return this.streamedRequest(endpoint, payload, "POST");
    }
    async upload(endpoint, formData, method = "PUT") {
        var _a;
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        const config = await this.buildRequestConfig(method, undefined);
        const proxyAgent = await this.getProxyAgent();
        const invoker = await this.getFetchInvoker();
        (_a = this.debugRequestObserver) === null || _a === void 0 ? void 0 : _a.call(this, { requestUrl: endpoint, requestMethod: method });
        const startTime = this.debugResponseObserver ? Date.now() : 0;
        // Make authorized ds request
        return new Promise((resolve, reject) => {
            try {
                const headers = { ...config.headers };
                // @ts-expect-error TS(2790): The operand of a 'delete' operator must be optiona... Remove this comment to see the full error message
                delete headers["Content-Type"];
                invoker(endpoint, {
                    method: config.method,
                    headers,
                    body: formData,
                    agent: proxyAgent,
                })
                    .then(async (response) => {
                    return this.handleResponse(response, endpoint);
                })
                    .then((jsonResponse) => {
                    var _a;
                    (_a = this.debugResponseObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
                        requestUrl: endpoint,
                        executionTime: Date.now() - startTime,
                    });
                    resolve(jsonResponse);
                })
                    .catch((error) => {
                    var _a;
                    (_a = this.debugResponseObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
                        requestUrl: endpoint,
                        executionTime: Date.now() - startTime,
                        error: { message: error === null || error === void 0 ? void 0 : error.message, stack: error === null || error === void 0 ? void 0 : error.stack },
                    });
                    reject(error);
                });
            }
            catch (error) {
                reject(error);
            }
        });
    }
    async mcpRequest(endpoint, payload, sessionId) {
        var _a, _b, _c;
        const config = await this.buildRequestConfig("POST", "application/json");
        const proxyAgent = await this.getProxyAgent();
        const invoker = await this.getFetchInvoker();
        const headers = {
            ...config.headers,
            Accept: "application/json, text/event-stream",
            "mcp-protocol-version": "2025-06-18",
            ...(sessionId ? { "mcp-session-id": sessionId } : {}),
        };
        (_a = this.debugRequestObserver) === null || _a === void 0 ? void 0 : _a.call(this, { requestUrl: endpoint, requestMethod: "POST" });
        const startTime = this.debugResponseObserver ? Date.now() : 0;
        try {
            const response = await invoker(endpoint, {
                method: "POST",
                headers,
                body: JSON.stringify(payload),
                agent: proxyAgent,
            });
            if (!response.ok) {
                const rawText = await response.text();
                throw parseErrorResponseBody(response.status, endpoint, rawText);
            }
            const text = await response.text();
            (_b = this.debugResponseObserver) === null || _b === void 0 ? void 0 : _b.call(this, {
                requestUrl: endpoint,
                executionTime: Date.now() - startTime,
            });
            return {
                message: this.parseMCPMessage(text),
                sessionId: response.headers.get("mcp-session-id"),
            };
        }
        catch (error) {
            (_c = this.debugResponseObserver) === null || _c === void 0 ? void 0 : _c.call(this, {
                requestUrl: endpoint,
                executionTime: Date.now() - startTime,
                error: { message: error === null || error === void 0 ? void 0 : error.message, stack: error === null || error === void 0 ? void 0 : error.stack },
            });
            throw error;
        }
    }
    parseMCPMessage(text) {
        var _a;
        if (!text) {
            return null;
        }
        const dataLine = (_a = text
            .split("\n")
            .find((line) => line.startsWith("data: "))) === null || _a === void 0 ? void 0 : _a.slice(6);
        const rawMessage = dataLine !== null && dataLine !== void 0 ? dataLine : text;
        return JSON.parse(rawMessage);
    }
    parseMCPToolText(message) {
        var _a, _b, _c, _d, _e;
        if (message === null || message === void 0 ? void 0 : message.error) {
            throw new Error((_a = message.error.message) !== null && _a !== void 0 ? _a : "MCP tool call failed");
        }
        const textContent = (_d = (_c = (_b = message === null || message === void 0 ? void 0 : message.result) === null || _b === void 0 ? void 0 : _b.content) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.text;
        if (textContent) {
            return textContent;
        }
        const structuredContent = (_e = message === null || message === void 0 ? void 0 : message.result) === null || _e === void 0 ? void 0 : _e.structuredContent;
        if (structuredContent) {
            return JSON.stringify(structuredContent);
        }
        return "";
    }
    async request(endpoint, payload, method, asData = false) {
        var _a;
        const config = await this.buildRequestConfig(method, "application/json");
        const proxyAgent = await this.getProxyAgent();
        const invoker = await this.getFetchInvoker();
        (_a = this.debugRequestObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
            requestUrl: endpoint,
            requestMethod: method,
            asData,
        });
        const startTime = this.debugResponseObserver ? Date.now() : 0;
        // Make authorized ds request
        return new Promise((resolve, reject) => {
            try {
                invoker(endpoint, {
                    method: config.method,
                    headers: config.headers,
                    body: payload ? JSON.stringify(payload) : null,
                    agent: proxyAgent,
                })
                    .then(async (response) => {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    return this.handleResponse(response, endpoint, asData);
                })
                    .then((response) => {
                    var _a;
                    (_a = this.debugResponseObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
                        requestUrl: endpoint,
                        executionTime: Date.now() - startTime,
                    });
                    resolve(response);
                })
                    .catch((error) => {
                    var _a;
                    (_a = this.debugResponseObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
                        requestUrl: endpoint,
                        executionTime: Date.now() - startTime,
                        error: { message: error === null || error === void 0 ? void 0 : error.message, stack: error === null || error === void 0 ? void 0 : error.stack },
                    });
                    reject(error);
                });
            }
            catch (error) {
                reject(error);
            }
        });
    }
    async streamedRequest(endpoint, payload, method, asData = false) {
        var _a;
        const config = await this.buildRequestConfig(method, "text/plain");
        const proxyAgent = await this.getProxyAgent();
        const invoker = await this.getFetchInvoker();
        (_a = this.debugRequestObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
            requestUrl: endpoint,
            requestMethod: method,
            isStreamed: true,
            asData,
        });
        const startTime = this.debugResponseObserver ? Date.now() : 0;
        // Make authorized ds request
        return new Promise((resolve, reject) => {
            try {
                invoker(endpoint, {
                    method: config.method,
                    headers: config.headers,
                    body: payload ? JSON.stringify(payload) : null,
                    agent: proxyAgent,
                })
                    .then(async (response) => {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    return this.handleStreamedResponse(response, endpoint);
                })
                    .then((response) => {
                    var _a;
                    (_a = this.debugResponseObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
                        requestUrl: endpoint,
                        executionTime: Date.now() - startTime,
                    });
                    resolve(response);
                })
                    .catch((error) => {
                    var _a;
                    (_a = this.debugResponseObserver) === null || _a === void 0 ? void 0 : _a.call(this, {
                        requestUrl: endpoint,
                        executionTime: Date.now() - startTime,
                        error: { message: error === null || error === void 0 ? void 0 : error.message, stack: error === null || error === void 0 ? void 0 : error.stack },
                    });
                    reject(error);
                });
            }
            catch (error) {
                reject(error);
            }
        });
    }
    async handleResponse(response, endpoint, asData = false) {
        if (!response.ok) {
            // Read the body as text first so a non-JSON error page does not throw
            const rawText = await response.text();
            throw parseErrorResponseBody(response.status, endpoint, rawText);
        }
        if (asData) {
            return response.arrayBuffer();
        }
        return response.json();
    }
    async handleStreamedResponse(response, endpoint) {
        var _a;
        if (!response.ok) {
            // Read the body as text first so a non-JSON error page does not throw
            const rawText = await response.text();
            throw parseErrorResponseBody(response.status, endpoint, rawText);
        }
        const reader = (_a = response.body) === null || _a === void 0 ? void 0 : _a.getReader();
        if (!reader) {
            throw new Error("Failed to get reader from response, likely using a non-streaming endpoint with streaming handler");
        }
        return reader;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Request configuration
    async buildRequestConfig(method, contentType) {
        var _a;
        const config = {
            method,
            headers: {
                ...(contentType && { "Content-Type": contentType }),
                Authorization: undefined,
                "x-sn-exporter-preview": this.isPreview ? "true" : undefined,
                "x-sn-export-job": this.jobId ? this.jobId : undefined,
                // Server-side secret injection
                ...(((_a = process === null || process === void 0 ? void 0 : process.env) === null || _a === void 0 ? void 0 : _a.SUPERNOVA_API_SECRET) && {
                    "X-SN-API-SECRET": process.env.SUPERNOVA_API_SECRET,
                }),
            },
        };
        let skipAuth = false;
        if (this.requestHook) {
            const hookResult = await this.requestHook(config);
            if (hookResult && hookResult.skipDefaultAuth) {
                skipAuth = true;
            }
        }
        if (!skipAuth || !this.authToken) {
            const token = this.authToken;
            // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'undefined... Remove this comment to see the full error message
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    }
    async getProxyAgent() {
        return this.customProxyLib ? this.customProxyLib : undefined;
    }
    getFetchInvoker() {
        return this.customFetchLib ? this.customFetchLib : fetch;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Endpoints
    getAIEndpoint(fragment) {
        return `${this.aiApiUrl}/${fragment}`;
    }
    getApiEndpoint(fragment) {
        return `${this.apiUrl}/${fragment}`;
    }
    getBaseEndpoint(fragment) {
        if (fragment) {
            return `${this.apiUrl}/${fragment}`;
        }
        return this.apiUrl;
    }
    getPublicEndpoint(fragment) {
        return `${this.apiUrl}/public/${fragment}`;
    }
    getAclEndpoint(resource) {
        if (!resource) {
            return this.getPublicEndpoint("acl");
        }
        return `${this.getPublicEndpoint("acl")}/${resource}`;
    }
    getMCPStreamEndpoint(fragment) {
        if (fragment) {
            return this.getApiEndpoint(`mcp/streams/${fragment}`);
        }
        return this.getApiEndpoint("mcp/streams");
    }
    getContextMCPEndpoint(contextId) {
        return `${this.getMCPServerUrl()}/mcp/c/${contextId}`;
    }
    getMCPServerUrl() {
        const apiUrl = new URL(this.apiUrl);
        const mcpHost = apiUrl.hostname.startsWith("api.")
            ? apiUrl.hostname.replace(/^api\./, "mcp.")
            : apiUrl.hostname;
        return `${apiUrl.protocol}//${mcpHost}`;
    }
    getUserEndpoint(userId, fragment) {
        if (fragment) {
            return `${this.apiUrl}/users/${userId}/${fragment}`;
        }
        return `${this.apiUrl}/users/${userId}`;
    }
    getUserWorkspaceEndpoint(userId, wsId, fragment) {
        if (fragment) {
            return `${this.apiUrl}/users/${userId}/workspaces/${wsId}/${fragment}`;
        }
        return `${this.apiUrl}/users/${userId}/workspaces/${wsId}`;
    }
    getWorkspaceEndpoint(wsId, fragment) {
        if (fragment) {
            return `${this.apiUrl}/workspaces/${wsId}/${fragment}`;
        }
        return `${this.apiUrl}/workspaces/${wsId}`;
    }
    getWorkspaceIntegrationCredentialEndpoint(wsId, integrationId, credentialId) {
        return `${this.getWorkspaceEndpoint(wsId, null)}/integrations/${integrationId}/credentials/${credentialId}`;
    }
    getWorkspaceIntegrationEndpoint(wsId, integrationId) {
        return `${this.getWorkspaceEndpoint(wsId, null)}/integrations/${integrationId}`;
    }
    getWorkspaceIntegrationGitProvidersEndpoint(wsId, integrationId, operation) {
        return `${this.getWorkspaceIntegrationEndpoint(wsId, integrationId)}/git/${operation}`;
    }
    getDesignSystemEndpoint(dsId, fragment) {
        if (fragment) {
            return `${this.apiUrl}/design-systems/${dsId}/${fragment}`;
        }
        return `${this.apiUrl}/design-systems/${dsId}`;
    }
    getVersionEndpoint(dsId, dsVersionId, fragment) {
        if (fragment) {
            return this.getDesignSystemEndpoint(dsId, `versions/${dsVersionId}/${fragment}`);
        }
        return this.getDesignSystemEndpoint(dsId, `versions/${dsVersionId}`);
    }
    getVersionCreationJobEndpoint(dsId, jobId) {
        return this.getDesignSystemEndpoint(dsId, `versions/job/${jobId}`);
    }
    getVersionCreationJobsEndpoint(dsId) {
        return this.getDesignSystemEndpoint(dsId, `versions/jobs`);
    }
}
